import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

import { usePrevious } from 'hooks';
import { useSelector } from 'react-redux';
import { AlarmsActions } from 'state/modules/alarms';
import Autocomplete from '@material-ui/lab/Autocomplete';
import AlarmsTypeSelectStyled from './AlarmsTypeSelect.styled';
import CircularProgress from '@material-ui/core/CircularProgress';
import { truncateKeywordSpaces } from 'utils/utils';

const AlarmsTypeSelect = ({
  label,
  variant,
  field: { name, value },
  id,
  form: { errors, touched, setFieldValue },
}) => {
  const dispatch = useDispatch();
  const [defaultValue, setDefaultValue] = useState([]);
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    actionable: true,
  });

  const { types, fetchingAlarmsTypes } = useSelector((state) => state.alarms);
  const prevvalue = usePrevious(value);

  const fetchAlarmsTypes = useCallback(
    debounce((params) => {
      if (
        params &&
        params.hasOwnProperty('keyword') &&
        (params.keyword === undefined || params.keyword === '')
      )
        delete params.keyword;
      const newParams = { ...params };
      setParams(newParams);
      if (newParams?.keyword) {
        newParams.keyword = truncateKeywordSpaces(newParams.keyword);
      }
      dispatch(AlarmsActions.fetchAlarmsTypes(newParams));
    }, 1000),
    []
  );

  useEffect(() => {
    if (!value) return;
    fetchAlarmsTypes(params);
  }, []) // eslint-disable-line

  useEffect(() => {
    if (prevvalue !== value || !defaultValue) {
      setDefaultValue(value || []);
    }
  }, [value, types]) // eslint-disable-line

  const handleFetchAlarmsTypes = () => {
    if (defaultValue.length === 0 && !fetchingAlarmsTypes && !params?.keyword)
      dispatch(AlarmsActions.fetchAlarmsTypes(params));
  };
  return (
    <Autocomplete
      multiple
      openOnFocus
      options={types}
      fullwidth="true"
      id="alarms-types"
      clearOnBlur={false}
      value={defaultValue}
      loading={fetchingAlarmsTypes}
      onHighlightChange={handleFetchAlarmsTypes}
      getOptionLabel={(option) => option.name || ''}
      onInputChange={(ev, val) => {
        fetchAlarmsTypes({ ...params, keyword: val });
      }}
      onChange={(ev, val) => {
        setFieldValue(name, val || []);
      }}
      getOptionSelected={(option, value) => option.id === value.id}
      renderInput={(params) => (
        <AlarmsTypeSelectStyled
          {...params}
          data-cy={id}
          label={label}
          margin="normal"
          variant={variant}
          error={errors && errors[name] && touched && touched[name]}
          helperText={errors && errors[name] && touched && touched[name] && errors[name]}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {fetchingAlarmsTypes ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

AlarmsTypeSelect.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

AlarmsTypeSelect.defaultProps = {
  required: true,
  disabled: false,
  form: {
    errors: {},
    touched: {},
  },
  label: 'Select...',
  variant: 'standard',
  id: 'alarms-types-select',
};

export default AlarmsTypeSelect;
